import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslationWithFallback from 'hooks/use-translation-with-fallback';

// Store
import { useSelector } from 'react-redux';
import { selectCurrentRestaurant } from 'store/slices/restaurantsSlice';

// Utils
import bemify from 'utils/bemify';
import formatPrice from 'utils/format-price';

// Hooks
import useAllergens from 'hooks/use-allergens';

// Components
import Button from 'components/button';
import Icon from 'components/icon';
import Image from 'components/image';
import QuantitySelector from 'components/quantity-selector';
import Tooltip from 'components/tooltip';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const ProductCard = ({
  allergensTranslations = null,
  className = null,
  onAddToCart = null,
  onClick = null,
  paddingSize = 'default',
  priceSize = 'default',
  productDetails = null,
  restaurantCurrency = null,
  selectedLocale,
  titleSize = 'default',
  translations
}) => {
  const { lang } = useTranslationWithFallback();

  const allAllergens = useAllergens(allergensTranslations);
  const { t: common } = useTranslationWithFallback('common');

  // States
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [formattedPrice, setFormattedPrice] = useState(null);

  // Selectors
  const currentRestaurant = useSelector(selectCurrentRestaurant);

  // Computed
  const classNames = `${styles.productCard}${
    onClick ? ` ${styles.productCard_isInteractive}` : ''}${
    paddingSize === 'md' ? ` ${styles.productCard_paddingSizeMedium}` : ''}${
    className ? ` ${className}` : ''}`;

  const priceClassNames = `${styles.productCard__price}${
    priceSize === 's' ? ` ${styles.productCard__price_small}` : ''}`;

  const titleClassNames = `${styles.productCard__title}${
    titleSize === 's' ? ` ${styles.productCard__title_small}` : ''}`;

  const {
    allergens,
    featured,
    imageUrl,
    peopleServeNumber,
    price
  } = productDetails || {};

  const hasAllergens = Array.isArray(allergens) && !!allergens.length;

  const productTitle = (
    productDetails?.translations?.[selectedLocale || lang]?.title ||
    productDetails?.fallbackTranslations?.title
  );

  const productDescription = (
    productDetails?.translations?.[selectedLocale || lang]?.description ||
    productDetails?.fallbackTranslations?.description
  );

  // Methods
  const handleAddToCartButtonClick = (event) => {
    event.preventDefault();

    onAddToCart(selectedQuantity);

    setSelectedQuantity(1);
  };

  const handleCardKeyDown = (event) => {
    // Perform action only if spacebar or enter is pressed
    if (event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault();

      onClick();
    }
  };

  // Effects
  useEffect(() => {
    setFormattedPrice(formatPrice(price, currentRestaurant?.currency || restaurantCurrency));
  }, [currentRestaurant, price, restaurantCurrency]);

  return (
    <div
      className={classNames}
      role={onClick ? 'button' : 'div'}
      tabIndex={onClick ? 0 : -1}
      onClick={onClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className={`${styles.productCard__content}${!imageUrl ? ` ${styles.productCard__content_noImage}` : ''}`}>
        <div className={styles.productCard__info}>
          <div>
            <div className={titleClassNames}>
              {productTitle}
            </div>
            {!!productDescription && (
              <div className={styles.productCard__description}>
                {productDescription}
              </div>
            )}
          </div>
          <div className={styles.productCard__priceAndAllergens}>
            {hasAllergens && (
              <div className={styles.productCard__allergensWrapper}>
                {allergens.map((entry, index) => {
                  const productAllergenDetails = allAllergens.find(allergen => allergen.id === entry);

                  if (!productAllergenDetails) return null;

                  return (
                    <Tooltip
                      key={index}
                      text={productAllergenDetails.label}
                      trigger={(
                        <div>
                          <Icon
                            className={styles.productCard__allergenIcon}
                            name={productAllergenDetails.icon}
                          />
                        </div>
                      )}
                    />
                  );
                })}
              </div>
            )}
            <div className={priceClassNames}>
              {formattedPrice}
            </div>
          </div>
        </div>
        {!!imageUrl && (
          <div className={styles.productCard__imageWrapper}>
            {(!!featured || !!peopleServeNumber) && (
              <div className={styles.productCard__icons}>
                {!!peopleServeNumber && (
                  <div className={styles.productCard__iconWrapper}>
                    <Icon
                      className={styles.productCard__icon}
                      name="user"
                    />
                    <div className={styles.productCard__peopleServeNumber}>
                      {peopleServeNumber}
                    </div>
                  </div>
                )}
                {!!featured && (
                  <div className={styles.productCard__iconWrapper}>
                    <Icon
                      className={styles.productCard__featuredIcon}
                      name="star"
                    />
                  </div>
                )}
              </div>
            )}
            <Image
              alt={productTitle}
              layout="fill"
              objectFit="cover"
              sizes="(min-width: 1400px) 330px, (min-width: 1200px) 400px, (min-width: 992px) 28vw, (min-width: 768px) 48vw, calc(100vw - 40px)"
              src={imageUrl}
            />
          </div>
        )}
      </div>
      {!!onAddToCart && (
        <footer className={styles.productCard__footer}>
          <QuantitySelector
            id={`quantity-selector-${productDetails.entityId}`}
            value={selectedQuantity}
            onChange={setSelectedQuantity}
          />
          <Button
            size="s"
            text={translations?.addToCart || common('actions.add_to_cart')}
            theme="primary"
            onClick={handleAddToCartButtonClick}
          />
        </footer>
      )}
    </div>
  );
};

ProductCard.propTypes = {
  allergensTranslations: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  onAddToCart: PropTypes.func,
  onClick: PropTypes.func,
  paddingSize: PropTypes.oneOf(['default', 'md']),
  priceSize: PropTypes.oneOf(['default', 's']),
  productDetails: PropTypes.shape({
    allergens: PropTypes.arrayOf(PropTypes.string),
    fallbackTranslations: PropTypes.shape({
      description: PropTypes.string,
      title: PropTypes.string
    }),
    featured: PropTypes.bool,
    imageUrl: PropTypes.string,
    ingredients: PropTypes.string,
    peopleServeNumber: PropTypes.number,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    translations: PropTypes.objectOf(PropTypes.shape({
      description: PropTypes.string,
      title: PropTypes.string
    }))
  }),
  restaurantCurrency: PropTypes.string,
  selectedLocale: PropTypes.string,
  titleSize: PropTypes.oneOf(['default', 's']),
  translations: PropTypes.shape({
    addToCart: PropTypes.string
  })
};

export default ProductCard;
